/**
 * Nav — hamburger menu for yan-vada PWA.
 *
 * Mobile: slide-out drawer from right (hamburger toggle).
 * Desktop: horizontal top bar with dropdown sections.
 *
 * Sections:
 *   Pitch          → / (sponsor pitch)
 *   Status         → /status (project progress dashboard)
 *   Docs           → scaffolded links to workspace docs
 *   Legal          → /privacy, /terms
 *   DGTL Team      → internal design/palette references
 *   Account        → /account or /signin (auth-aware)
 */

import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import NeonWordmark from './NeonWordmark'
import NotificationBellIcon from './NotificationBellIcon'

/* ---------- Types ---------- */

interface NavLink {
  label: string
  to?: string
  action?: 'signOut'
  href?: string
  external?: boolean
  badge?: string
  disabled?: boolean
}

interface NavSection {
  title?: string
  items: NavLink[]
}

interface NotificationListItem {
  id: string
  title: string
  body: string
  priority: string
  read_at: string | null
  seen_at: string | null
  requires_ack?: boolean
  acknowledged_at?: string | null
  data?: { href?: string }
}

interface NotificationMutationResponse {
  notification?: Partial<NotificationListItem>
  acknowledgedAt?: string | null
  seenAt?: string | null
  error?: string
}

const NOTIFICATION_POPOVER_LIMIT = 8

/* ---------- Data ---------- */

function HomeIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3 10.5 12 3l9 7.5" />
      <path d="M6.75 9.75V21h10.5V9.75" />
      <path d="M10 21v-6h4v6" />
    </svg>
  )
}

function BackIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M14.5 5 7.5 12l7 7" />
      <path d="M8.25 12H20" />
    </svg>
  )
}

function buildSections(isLoggedIn: boolean, knownMachine: boolean, email: string | null, role: string | null, status: string | null, rights: Record<string, boolean>, unreadCount: number, attentionCount: number): NavSection[] {
  if (!isLoggedIn && !knownMachine) {
    return [{
      title: 'Account',
      items: [{ label: 'Sign in', to: '/signin' }],
    }]
  }

  const isRoleAdmin = ['founder', 'admin'].includes(role ?? '')
  const isDgtlTeam = isLoggedIn && status === 'approved' && (isRoleAdmin || email?.toLowerCase().endsWith('@dgtl.lk'))
  const adminItems: NavLink[] = []

  if (isLoggedIn && status === 'approved') {
    if (
      rights['admin.members.manage']
      || rights['admin.content.manage']
      || rights['integrations.delivery.manage']
      || rights['admin.knockout.manage']
      || rights['pitch.view_all']
    ) adminItems.push({ label: 'Command Center', to: '/admin' })
    if (rights['admin.content.manage']) adminItems.push({ label: 'Master Pitch CMS', to: '/admin/sponsor-pages/master' })
    if (rights['pitch.view_all']) adminItems.push({ label: 'View Master Pitch', to: '/sponsors/master' })
    if (rights['admin.members.manage']) adminItems.push({ label: 'Members CMS', to: '/admin/members' })
    if (rights['integrations.delivery.manage']) adminItems.push({ label: 'Integrations', to: '/admin/integrations' })
    if (rights['admin.knockout.manage']) adminItems.push({ label: 'Knockout workbench', to: '/admin/knockout' })
  }

  return [
    {
      items: [
        { label: 'Public home', to: '/' },
        { label: 'Project Status', to: '/status' },
      ],
    },
    {
      title: 'Legal',
      items: [
        { label: 'Privacy Policy', to: '/privacy' },
        { label: 'Terms of Use', to: '/terms' },
        { label: 'Sponsor NDA', to: '/nda', disabled: true, badge: 'soon' },
      ],
    },
    ...(isDgtlTeam ? [{
      title: 'DGTL Team',
      items: [
        { label: 'Design & palette docs', to: '/team/design' },
        { label: 'Design sprint brief', to: '/team/design', badge: 'Claude Design' },
      ],
    }] : []),
    ...(adminItems.length ? [{
      title: 'Admin',
      items: adminItems,
    }] : []),
    {
      title: 'Account',
      items: isLoggedIn
        ? [
            { label: 'Profile', to: '/account' },
            { label: 'Notifications', to: '/notifications', badge: unreadCount > 0 ? String(unreadCount) : (attentionCount > 0 ? 'ack' : undefined) },
            { label: 'Sign out', action: 'signOut' },
          ]
        : [
            { label: 'Sign in', to: '/signin' },
            { label: 'Request access', to: '/request-access' },
          ],
    },
  ]
}

/* ---------- Component ---------- */

export default function Nav() {
  const [open, setOpen] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const { isLoggedIn, knownMachine, email, role, status, rights } = useAuth()
  const [unreadCount, setUnreadCount] = useState(0)
  const [attentionCount, setAttentionCount] = useState(0)
  const [notificationOpen, setNotificationOpen] = useState(false)
  const [notificationItems, setNotificationItems] = useState<NotificationListItem[]>([])
  const [notificationsLoading, setNotificationsLoading] = useState(false)
  const [notificationActionError, setNotificationActionError] = useState<string | null>(null)
  const [notificationPulse, setNotificationPulse] = useState(false)
  const notificationButtonRef = useRef<HTMLButtonElement | null>(null)
  const notificationPopoverRef = useRef<HTMLDivElement | null>(null)
  const previousNotificationTotalRef = useRef(0)
  const notificationCountReadyRef = useRef(false)
  const notificationPulseTimerRef = useRef<number | null>(null)
  const sections = buildSections(isLoggedIn, knownMachine, email, role, status, rights, unreadCount, attentionCount)
  const showNotificationButton = isLoggedIn
  const isHomeRoute = location.pathname === '/' || location.pathname === '/home'

  function applyNotificationCounts(nextUnreadCount: number, nextAttentionCount: number, animate = true) {
    const nextTotal = nextUnreadCount + nextAttentionCount
    const previousTotal = previousNotificationTotalRef.current
    if (animate && notificationCountReadyRef.current && nextTotal > previousTotal) {
      setNotificationPulse(true)
      if (notificationPulseTimerRef.current) window.clearTimeout(notificationPulseTimerRef.current)
      notificationPulseTimerRef.current = window.setTimeout(() => setNotificationPulse(false), 1400)
    }
    previousNotificationTotalRef.current = nextTotal
    notificationCountReadyRef.current = true
    setUnreadCount(nextUnreadCount)
    setAttentionCount(nextAttentionCount)
  }

  async function refreshNotificationCounts(animate = true) {
    const res = await fetch('/api/member/notifications/count', { credentials: 'include' })
    const data = await res.json().catch(() => ({})) as { unreadCount?: number; attentionCount?: number }
    applyNotificationCounts(data.unreadCount ?? 0, data.attentionCount ?? 0, animate)
  }

  useEffect(() => {
    if (!isLoggedIn) {
      applyNotificationCounts(0, 0, false)
      setNotificationOpen(false)
      return
    }
    let cancelled = false
    async function loadUnreadCount() {
      const res = await fetch('/api/member/notifications/count', { credentials: 'include' })
      const data = await res.json().catch(() => ({})) as { unreadCount?: number; attentionCount?: number }
      if (!cancelled) applyNotificationCounts(data.unreadCount ?? 0, data.attentionCount ?? 0)
    }
    void loadUnreadCount().catch(() => undefined)
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void loadUnreadCount().catch(() => undefined)
    }, 12 * 1000)
    return () => {
      cancelled = true
      window.clearInterval(timer)
      if (notificationPulseTimerRef.current) window.clearTimeout(notificationPulseTimerRef.current)
    }
  }, [isLoggedIn, location.pathname])

  useEffect(() => {
    if (!notificationOpen) return undefined

    function handlePointerDown(event: PointerEvent) {
      const target = event.target as Node | null
      if (target && (notificationButtonRef.current?.contains(target) || notificationPopoverRef.current?.contains(target))) return
      setNotificationOpen(false)
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setNotificationOpen(false)
    }

    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [notificationOpen])

  useEffect(() => {
    if (!open) return undefined
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open])

  function handleNav(to: string) {
    setOpen(false)
    setNotificationOpen(false)
    navigate(to)
  }

  function handleAction(action: NonNullable<NavLink['action']>) {
    setOpen(false)
    if (action === 'signOut') {
      const next = `${location.pathname}${location.search}`
      window.location.assign(`/api/auth/full-signout?next=${encodeURIComponent(next || '/')}`)
    }
  }

  const isActive = (to: string) => location.pathname === to

  function handleHome() {
    setOpen(false)
    setNotificationOpen(false)
    navigate('/')
  }

  function handleBack() {
    setOpen(false)
    setNotificationOpen(false)
    if (window.history.length > 1 && !isHomeRoute) {
      navigate(-1)
      return
    }
    navigate('/')
  }

  async function openNotifications() {
    if (notificationOpen) {
      setNotificationOpen(false)
      return
    }
    setNotificationsLoading(true)
    setNotificationActionError(null)
    setNotificationOpen(true)
    try {
      const res = await fetch('/api/member/notifications', { credentials: 'include' })
      const data = await res.json().catch(() => ({})) as { notifications?: NotificationListItem[]; unreadCount?: number; attentionCount?: number; error?: string }
      if (!res.ok) throw new Error(data.error ?? 'Unable to load notifications')
      const items = data.notifications ?? []
      setNotificationItems(items.slice(0, NOTIFICATION_POPOVER_LIMIT))
      applyNotificationCounts(data.unreadCount ?? 0, data.attentionCount ?? 0, false)
      if ((data.unreadCount ?? 0) > 0) {
        const ids = items.filter(item => !item.seen_at).map(item => item.id)
        const seenRes = await fetch('/api/member/notifications/seen', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ ids }),
        })
        const seenData = await seenRes.json().catch(() => ({})) as NotificationMutationResponse
        if (!seenRes.ok) throw new Error(seenData.error ?? 'Unable to mark notifications seen')
        applyNotificationCounts(0, data.attentionCount ?? 0, false)
        const seenAt = new Date().toISOString()
        setNotificationItems(prev => prev.map(item => ({ ...item, seen_at: item.seen_at ?? seenAt })))
      }
    } catch (err) {
      setNotificationActionError(err instanceof Error ? err.message : 'Unable to load notifications')
    } finally {
      setNotificationsLoading(false)
    }
  }

  async function toggleNotificationSeen(item: NotificationListItem) {
    const nextSeen = !item.seen_at
    setNotificationActionError(null)
    try {
      const res = await fetch('/api/member/notifications/seen', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ id: item.id, seen: nextSeen }),
      })
      const data = await res.json().catch(() => ({})) as NotificationMutationResponse
      if (!res.ok) throw new Error(data.error ?? 'Unable to update marker')
      const seenAt = nextSeen ? (data.seenAt ?? new Date().toISOString()) : null
      setNotificationItems(prev => prev.map(nextItem => nextItem.id === item.id ? { ...nextItem, seen_at: seenAt } : nextItem))
      await refreshNotificationCounts(false).catch(() => undefined)
    } catch (err) {
      setNotificationActionError(err instanceof Error ? err.message : 'Unable to update marker')
    }
  }

  async function toggleNotificationDone(item: NotificationListItem) {
    if (!item.requires_ack) return
    const nextDone = !item.acknowledged_at
    setNotificationActionError(null)
    try {
      const res = await fetch('/api/member/notifications/acknowledge', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ id: item.id, acknowledged: nextDone }),
      })
      const data = await res.json().catch(() => ({})) as NotificationMutationResponse
      if (!res.ok) throw new Error(data.error ?? 'Unable to update task')
      const doneAt = nextDone ? (data.acknowledgedAt ?? new Date().toISOString()) : null
      setNotificationItems(prev => prev.map(nextItem => nextItem.id === item.id ? {
        ...nextItem,
        read_at: nextDone ? (nextItem.read_at ?? doneAt) : nextItem.read_at,
        seen_at: nextDone ? (nextItem.seen_at ?? doneAt) : nextItem.seen_at,
        acknowledged_at: doneAt,
      } : nextItem))
      applyNotificationCounts(unreadCount, Math.max(0, attentionCount + (nextDone ? -1 : 1)), false)
      await refreshNotificationCounts(false).catch(() => undefined)
    } catch (err) {
      setNotificationActionError(err instanceof Error ? err.message : 'Unable to update task')
    }
  }

  async function markAllNotificationsDone() {
    setNotificationActionError(null)
    try {
      const res = await fetch('/api/member/notifications/acknowledge', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ all: true }),
      })
      const data = await res.json().catch(() => ({})) as NotificationMutationResponse
      if (!res.ok) throw new Error(data.error ?? 'Unable to mark tasks done')
      const doneAt = data.acknowledgedAt ?? new Date().toISOString()
      setNotificationItems(prev => prev.map(item => item.requires_ack && !item.acknowledged_at ? {
        ...item,
        read_at: item.read_at ?? doneAt,
        seen_at: item.seen_at ?? doneAt,
        acknowledged_at: doneAt,
      } : item))
      applyNotificationCounts(unreadCount, 0, false)
      await refreshNotificationCounts(false).catch(() => undefined)
    } catch (err) {
      setNotificationActionError(err instanceof Error ? err.message : 'Unable to mark tasks done')
    }
  }

  return (
    <>
      {isLoggedIn && (
        <button
          ref={notificationButtonRef}
          className={`nav-notification-button${unreadCount > 0 ? ' has-notifications' : ''}${notificationPulse ? ' pulse' : ''}`}
          onClick={() => void openNotifications()}
          aria-label={unreadCount > 0 ? `${unreadCount} unread notifications` : (attentionCount > 0 ? `${attentionCount} notifications to do` : 'Notifications')}
          aria-expanded={notificationOpen}
        >
          <NotificationBellIcon />
          {unreadCount > 0 && <span className="nav-notification-count">{unreadCount}</span>}
          {unreadCount === 0 && attentionCount > 0 && <span className="nav-notification-attention">!</span>}
        </button>
      )}

      {!open && (
        <>
          <button
            className="nav-utility-button nav-home-button"
            type="button"
            onClick={handleHome}
            aria-label="Home"
          >
            <HomeIcon />
          </button>

          <button
            className={`nav-utility-button nav-back-button${isHomeRoute ? ' is-disabled' : ''}`}
            type="button"
            onClick={handleBack}
            aria-label="Back"
            disabled={isHomeRoute}
          >
            <BackIcon />
          </button>
        </>
      )}

      {notificationOpen && (
        <div ref={notificationPopoverRef} className="nav-notification-popover" role="dialog" aria-label="Notifications">
          <div className="nav-notification-popover-header">
            <div>
              <strong>Notifications</strong>
              {attentionCount > 0 && <span>{attentionCount} to do</span>}
            </div>
            {attentionCount > 0 && (
              <button className="nav-notification-mark-all" type="button" onClick={() => void markAllNotificationsDone()}>
                Mark all done
              </button>
            )}
          </div>
          {notificationsLoading ? (
            <p className="nav-notification-empty">Loading notifications...</p>
          ) : notificationItems.length === 0 ? (
            <p className="nav-notification-empty">No notifications yet.</p>
          ) : (
            <div className="nav-notification-list">
              {notificationActionError && <p className="nav-notification-error" role="alert">{notificationActionError}</p>}
              {notificationItems.map(item => (
                <div
                  key={item.id}
                  className={`nav-notification-item${item.seen_at ? ' seen' : ''}${item.requires_ack && !item.acknowledged_at ? ' needs-ack' : ''}${item.acknowledged_at ? ' done' : ''}`}
                >
                  <button className="nav-notification-item-copy" type="button" onClick={() => handleNav(item.data?.href ?? '/notifications')}>
                    <span>{item.title}</span>
                    <small>{item.body}</small>
                  </button>
                  <div className="nav-notification-item-controls" aria-label="Notification state controls">
                    <button
                      className={`nav-notification-state-toggle dot${item.seen_at ? '' : ' active'}`}
                      type="button"
                      aria-pressed={!item.seen_at}
                      aria-label={item.seen_at ? 'Mark as new' : 'Mark as seen'}
                      onClick={() => void toggleNotificationSeen(item)}
                    >
                      <span aria-hidden="true" />
                    </button>
                    {item.requires_ack && (
                      <button
                        className={`nav-notification-state-toggle done${item.acknowledged_at ? ' active' : ''}`}
                        type="button"
                        aria-pressed={Boolean(item.acknowledged_at)}
                        aria-label={item.acknowledged_at ? 'Mark as not done' : 'Mark as done'}
                        onClick={() => void toggleNotificationDone(item)}
                      >
                        <span aria-hidden="true">✓</span>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          <button className="nav-notification-view-all" type="button" onClick={() => handleNav('/notifications')}>
            View all notifications
          </button>
        </div>
      )}

      {/* hamburger button */}
      <button
        className={`nav-hamburger${showNotificationButton && (unreadCount > 0 || attentionCount > 0) ? ' has-notifications' : ''}`}
        onClick={() => setOpen(!open)}
        aria-label={open ? 'Close menu' : 'Open menu'}
        aria-expanded={open}
      >
        <span className={`nav-hamburger-line${open ? ' open' : ''}`} />
        <span className={`nav-hamburger-line${open ? ' open' : ''}`} />
        <span className={`nav-hamburger-line${open ? ' open' : ''}`} />
        {showNotificationButton && (unreadCount > 0 || attentionCount > 0) && <span className="nav-notification-dot" aria-hidden="true" />}
      </button>

      {/* backdrop */}
      {open && (
        <div className="nav-backdrop" onClick={() => setOpen(false)} />
      )}

      {/* drawer */}
      {open && <nav className="nav-drawer open" aria-label="Main navigation">
        <div className="nav-drawer-header">
          <div className="nav-drawer-utility-row">
            <button
              className={`nav-utility-button nav-drawer-utility-button nav-drawer-back-button${isHomeRoute ? ' is-disabled' : ''}`}
              type="button"
              onClick={handleBack}
              aria-label="Back"
              disabled={isHomeRoute}
            >
              <BackIcon />
            </button>
            <button
              className="nav-utility-button nav-drawer-utility-button nav-drawer-home-button"
              type="button"
              onClick={handleHome}
              aria-label="Home"
            >
              <HomeIcon />
            </button>
          </div>
          <button className="nav-drawer-brand" type="button" onClick={() => handleNav('/')}>
            <span aria-hidden="true"><NeonWordmark id="neon-drawer-brand" height={34} /></span>
            <span className="nav-drawer-brand-text">Yan-Vada</span>
          </button>
          {isLoggedIn && email && (
            <span className="nav-drawer-email">{email}</span>
          )}
        </div>

        <div className="nav-drawer-body">
          {sections.map((section, si) => (
            <div key={si} className="nav-section">
              {section.title && (
                <span className="nav-section-title">{section.title}</span>
              )}
              {section.items.map((item, ii) => (
                <div key={ii}>
                  {item.action && !item.disabled ? (
                    <button
                      className="nav-item"
                      onClick={() => handleAction(item.action!)}
                    >
                      {item.label}
                      {item.badge && <span className="nav-badge">{item.badge}</span>}
                    </button>
                  ) : item.to && !item.disabled ? (
                    <button
                      className={`nav-item${isActive(item.to) ? ' active' : ''}`}
                      onClick={() => handleNav(item.to!)}
                    >
                      {item.label}
                      {item.badge && <span className="nav-badge">{item.badge}</span>}
                    </button>
                  ) : item.href || item.disabled ? (
                    <span className="nav-item nav-item-disabled">
                      {item.label}
                      {item.badge && <span className="nav-badge nav-badge-muted">{item.badge}</span>}
                    </span>
                  ) : null}
                </div>
              ))}
            </div>
          ))}
        </div>

        <div className="nav-drawer-footer">
          <span className="nav-version">v0.1 &middot; Session 16</span>
        </div>
      </nav>}
    </>
  )
}
